import { useState } from "react";
import { api, formatApiError } from "@/lib/kfmApi";
import { useAuth } from "@/context/AuthContext";
import { useTrackedLinks, trackedIntent, trackedCopyLink, trackedCaption } from "@/lib/utm";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Share2, Copy, Check, Loader2, Twitter, Facebook, MessageCircle, Mail, Linkedin, Instagram, Send, Eye } from "lucide-react";
import { toast } from "sonner";
import ShareImagePicker from "@/components/ShareImagePicker";

// Self-contained "Share" trigger + dialog for a study session.
// Only a teaser is shared publicly — notes, prayers and the Go Deeper
// conversation are never included.
export default function ShareStudyDialog({ sessionId, title = "", testid = "share-study" }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [share, setShare] = useState(null); // { hook, url, read_url }
  const [copied, setCopied] = useState(false);
  const [imgV, setImgV] = useState(0); // bumps the FB preview when the image changes
  const [opts, setOpts] = useState({ include_chat: false, include_notes: false, include_answers: false, include_highlights: false, show_name: false });
  const [savingOpts, setSavingOpts] = useState(false);

  const applyOpts = (d) => setOpts({
    include_chat: !!d.include_chat, include_notes: !!d.include_notes,
    include_answers: !!d.include_answers, include_highlights: !!d.include_highlights, show_name: !!d.show_name,
  });

  const ensureShare = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/study/sessions/${sessionId}/share`);
      if (data.shared) {
        setShare(data);
        applyOpts(data);
      } else {
        const res = await api.post(`/study/sessions/${sessionId}/share`);
        setShare(res.data);
        applyOpts(res.data);
      }
      setImgV(Date.now());
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't create a share link");
    }
    setLoading(false);
  };

  const toggleOpt = async (key) => {
    const next = { ...opts, [key]: !opts[key] };
    const prev = opts;
    setOpts(next);
    setSavingOpts(true);
    try {
      const { data } = await api.post(`/study/sessions/${sessionId}/share`, next);
      setShare((s) => ({ ...s, ...data }));
    } catch (e) {
      setOpts(prev);
      toast.error("Couldn't update what's shared — please try again");
    }
    setSavingOpts(false);
  };

  const onOpen = () => {
    setOpen(true);
    if (!share) ensureShare();
  };

  const link = share?.url || "";
  const hook = share?.hook || "";
  const tracked = useTrackedLinks(link, "study_share");
  const copyTarget = trackedCopyLink(tracked, link, "study_share");

  let previewDomain = "openwordai.com";
  try { previewDomain = new URL(share?.read_url || link).host || previewDomain; } catch { /* noop */ }
  const fbImage = share?.image_url
    ? `${share.image_url}${share.image_url.includes("?") ? "&" : "?"}cb=${imgV || 0}`
    : "";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(copyTarget);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      toast.success("Link copied");
    } catch {
      toast.error("Couldn't copy — select and copy the link manually");
    }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "A Bible study on OpenWord AI", text: hook, url: copyTarget });
      } catch {
        /* user dismissed */
      }
    } else {
      copy();
    }
  };

  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailSending, setEmailSending] = useState(false);

  const sendEmail = async () => {
    const to = emailTo.trim();
    if (!to || !to.includes("@")) { toast.error("Enter a valid email address"); return; }
    setEmailSending(true);
    try {
      await api.post(`/study/sessions/${sessionId}/share/email`, { to });
      toast.success(`Study sent to ${to}`);
      setEmailTo("");
      setEmailOpen(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't send the email");
    }
    setEmailSending(false);
  };

  const copyInstagram = async () => {
    try {
      await navigator.clipboard.writeText(trackedCaption(tracked, link, hook, "study_share"));
      toast.success("Caption + link copied — paste it into your Instagram post or story");
    } catch {
      toast.error("Couldn't copy the caption");
    }
  };

  const targets = [
    { id: "x", label: "Share on X", Icon: Twitter, href: trackedIntent("x", tracked, link, hook, "study_share") },
    { id: "facebook", label: "Share on Facebook", Icon: Facebook, href: trackedIntent("fb", tracked, link, hook, "study_share") },
    { id: "whatsapp", label: "Share on WhatsApp", Icon: MessageCircle, href: trackedIntent("wa", tracked, link, hook, "study_share") },
    { id: "linkedin", label: "Share on LinkedIn", Icon: Linkedin, href: trackedIntent("li", tracked, link, hook, "study_share") },
  ];

  const unshare = async () => {
    try {
      await api.delete(`/study/sessions/${sessionId}/share`);
      setShare(null);
      setOpen(false);
      toast.success("This study is no longer shared");
    } catch {
      toast.error("Couldn't stop sharing");
    }
  };

  return (
    <>
      <button
        data-testid={`${testid}-open`}
        onClick={onOpen}
        className="flex items-center gap-1.5 text-sm font-medium text-[#07090E] bg-[#00E5FF] hover:bg-[#00B8CC] px-4 py-2 rounded-full transition-colors shadow-sm"
      >
        <Share2 className="w-4 h-4" strokeWidth={1.5} /> Share
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid={`${testid}-dialog`} className="bg-[#0E131F] sm:max-w-md rounded-2xl max-h-[90dvh] overflow-y-auto w-[calc(100vw-2rem)] p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="text-2xl text-white">Share this study</DialogTitle>
            <DialogDescription className="text-sm text-slate-400">
              Share a teaser to social media. By default only the study is public — you can also
              choose to include your own conversation, notes or reflections below.
            </DialogDescription>
          </DialogHeader>

          {loading || !share ? (
            <div className="py-12 flex flex-col items-center gap-3 text-slate-500">
              <Loader2 className="w-6 h-6 animate-spin text-[#00E5FF]" strokeWidth={1.5} />
              <span className="text-sm">Preparing a shareable teaser…</span>
            </div>
          ) : (
            <div className="space-y-5">
              <p className="text-sm text-slate-400 leading-relaxed">
                A short teaser is shared publicly. Choose below whether to also include your
                Go Deeper conversation, notes or reflections.
              </p>

              {typeof share.views === "number" && (
                <div data-testid={`${testid}-views`} className="flex items-center gap-2 text-sm text-[#00E5FF] bg-[#10222A]/60 rounded-xl px-3 py-2">
                  <Eye className="w-4 h-4" strokeWidth={1.75} />
                  {share.views === 0 ? (
                    <span>Not opened yet — share the link to see it here.</span>
                  ) : (
                    <span>
                      Opened <strong>{share.views}</strong> {share.views === 1 ? "time" : "times"}
                      {share.last_viewed_at ? ` · last opened ${new Date(share.last_viewed_at).toLocaleDateString()}` : ""}
                    </span>
                  )}
                </div>
              )}

              {/* Opt-in: include personal parts of the study in the public view. All default off. */}
              <div data-testid={`${testid}-include`} className="rounded-xl border border-[#29344D] p-4">
                <div className="text-xs font-medium uppercase tracking-widest text-slate-500 mb-2">
                  What to include
                </div>
                {[
                  { key: "include_chat", label: "My Go Deeper conversation", hint: "The questions you asked and the companion's replies" },
                  { key: "include_notes", label: "My notes", hint: "Your personal study notes" },
                  { key: "include_answers", label: "My reflection answers", hint: "How you answered the reflection questions" },
                  { key: "include_highlights", label: "My highlights & notes", hint: "Text you highlighted and any handwritten notes you attached" },
                  { key: "show_name", label: "Show my name", hint: `Adds “Shared by ${(user?.name || "your name")}” under the title` },
                ].map((row) => (
                  <button
                    key={row.key}
                    type="button"
                    data-testid={`${testid}-opt-${row.key}`}
                    onClick={() => toggleOpt(row.key)}
                    disabled={savingOpts}
                    aria-pressed={opts[row.key]}
                    className="w-full flex items-start gap-3 py-2 text-left group disabled:opacity-60"
                  >
                    <span className={`mt-0.5 w-5 h-5 rounded-md border flex items-center justify-center shrink-0 transition-colors ${opts[row.key] ? "bg-[#00E5FF] border-[#00E5FF]" : "border-[#3A4763] group-hover:border-[#00E5FF]"}`}>
                      {opts[row.key] && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm text-slate-300">{row.label}</span>
                      <span className="block text-xs text-slate-500 leading-snug">{row.hint}</span>
                    </span>
                  </button>
                ))}
                <p className="text-[11px] text-slate-500 leading-relaxed pt-1">
                  Everything is off by default. Anything you tick becomes visible to anyone with the link.
                </p>
              </div>

              <div
                data-testid={`${testid}-hook`}
                className="rounded-xl bg-[#10222A] border border-[#00E5FF]/15 p-4 text-sm text-slate-300 leading-relaxed italic"
              >
                {hook}
              </div>

              <ShareImagePicker token={share.token} imageUrl={share.image_url} initialCustom={share.custom_image} testid={`${testid}-img`} onChange={(t) => setImgV(t)} />

              {/* How the link will look when posted to Facebook */}
              <div data-testid={`${testid}-fb-preview`} className="rounded-xl border border-[#29344D] overflow-hidden">
                <div className="px-3 pt-3 pb-2 text-xs font-medium uppercase tracking-widest text-slate-500">
                  Preview on Facebook
                </div>
                <div className="border-t border-[#1E273D]">
                  <div className="w-full aspect-[1.91/1] bg-[#141B29]">
                    {fbImage && (
                      <img
                        src={fbImage}
                        alt="Facebook link preview"
                        className="w-full h-full object-cover"
                        data-testid={`${testid}-fb-preview-img`}
                      />
                    )}
                  </div>
                  <div className="bg-[#0E131F] px-3 py-2.5">
                    <div className="text-[11px] uppercase tracking-wide text-slate-400 truncate">{previewDomain}</div>
                    <div className="text-sm font-semibold text-white leading-snug line-clamp-2">
                      {title || "A Bible study on OpenWord AI"}
                    </div>
                    {hook && <div className="text-xs text-slate-400 leading-snug line-clamp-2 mt-0.5">{hook}</div>}
                  </div>
                </div>
                <p className="px-3 py-2 text-[11px] text-slate-500 leading-relaxed">
                  This is roughly how your link will appear when posted. Facebook may take a little while to refresh the image the first time.
                </p>
              </div>

              <div className="flex items-center gap-2">
                <input
                  data-testid={`${testid}-link`}
                  readOnly
                  value={link}
                  onFocus={(e) => e.target.select()}
                  className="flex-1 h-11 rounded-xl border border-[#29344D] bg-[#0B0F16] px-3 text-sm text-slate-400 truncate focus:outline-none"
                />
                <button
                  data-testid={`${testid}-copy`}
                  onClick={copy}
                  className="h-11 w-11 shrink-0 rounded-xl bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E] flex items-center justify-center transition-colors"
                  title="Copy link"
                >
                  {copied ? <Check className="w-5 h-5" strokeWidth={1.5} /> : <Copy className="w-5 h-5" strokeWidth={1.5} />}
                </button>
              </div>

              <div className="space-y-2">
                <button
                  data-testid={`${testid}-native`}
                  onClick={nativeShare}
                  className="w-full h-11 rounded-full bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E] text-sm font-medium flex items-center justify-center gap-2 transition-colors"
                >
                  <Share2 className="w-4 h-4" strokeWidth={1.5} /> Share…
                </button>
                <div className="flex items-center flex-wrap gap-2">
                  {targets.map((t) => (
                    <a
                      key={t.id}
                      data-testid={`${testid}-${t.id}`}
                      href={t.href}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={t.label}
                      title={t.label}
                      className="w-11 h-11 shrink-0 flex items-center justify-center rounded-full bg-[#10222A] text-[#00E5FF] hover:bg-[#10222A] transition-colors"
                    >
                      <t.Icon className="w-4 h-4" strokeWidth={1.5} />
                    </a>
                  ))}
                  <button
                    data-testid={`${testid}-instagram`}
                    onClick={copyInstagram}
                    aria-label="Copy caption for Instagram"
                    title="Instagram has no direct web share — copy the caption + link to paste"
                    className="w-11 h-11 shrink-0 flex items-center justify-center rounded-full bg-[#10222A] text-[#00E5FF] hover:bg-[#10222A] transition-colors"
                  >
                    <Instagram className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                  <button
                    data-testid={`${testid}-email`}
                    onClick={() => setEmailOpen((v) => !v)}
                    aria-label="Send by email"
                    title="Send this study by email"
                    className="w-11 h-11 shrink-0 flex items-center justify-center rounded-full bg-[#10222A] text-[#00E5FF] hover:bg-[#10222A] transition-colors"
                  >
                    <Mail className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                </div>
              </div>

              {emailOpen && (
                <div data-testid={`${testid}-email-form`} className="flex items-center gap-2">
                  <input
                    data-testid={`${testid}-email-input`}
                    type="email"
                    value={emailTo}
                    onChange={(e) => setEmailTo(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && sendEmail()}
                    placeholder="friend@example.com"
                    className="flex-1 h-11 rounded-xl border border-[#29344D] bg-[#0B0F16] px-3 text-sm text-slate-400 focus:outline-none focus:border-[#00E5FF]/50"
                  />
                  <button
                    data-testid={`${testid}-email-send`}
                    onClick={sendEmail}
                    disabled={emailSending}
                    className="h-11 px-4 shrink-0 rounded-xl bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E] text-sm font-medium flex items-center justify-center gap-1.5 transition-colors disabled:opacity-50"
                  >
                    {emailSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" strokeWidth={1.5} />} Send
                  </button>
                </div>
              )}

              <button
                data-testid={`${testid}-unshare`}
                onClick={unshare}
                className="w-full text-sm text-slate-500 hover:text-red-500 transition-colors"
              >
                Stop sharing this study
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
